import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, MoreHorizontal, Trash2, Loader2, UserPlus, Copy, Eye, EyeOff, CheckCircle, KeyRound, Pencil, Shield, Mail } from 'lucide-react';
import { toast } from 'sonner';
import CSVImportModal from '../components/CSVImportModal';
import CSVExportButton from '../components/CSVExportButton';

const AgentsPage = () => {
  const { api, user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [newAgentCredentials, setNewAgentCredentials] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  
  // Edit agent state
  const [editOpen, setEditOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    is_active: true
  });
  
  // Reset password state
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetAgentId, setResetAgentId] = useState(null);
  const [resetAgentName, setResetAgentName] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      // Use the unified endpoint so both admin-created (`db.agents`) and
      // approved public-signup (`db.users` user_type=agent) rows show here.
      const response = await api().get('/agents/unified');
      setAgents(response.data || []);
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null
      };
      
      // Only include password if provided
      if (formData.password && formData.password.length >= 6) {
        submitData.password = formData.password;
      }
      
      const response = await api().post('/agents', submitData);
      toast.success('Agent created successfully');
      setCreateOpen(false);
      setFormData({ name: '', email: '', phone: '', password: '' });
      
      // Show credentials
      setNewAgentCredentials({
        username: response.data.username,
        password: response.data.generated_password || response.data.plain_password
      });
      setCredentialsOpen(true);
      
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingAgent) return;
    
    try {
      await api().put(`/agents/${editingAgent.id}`, editFormData);
      toast.success('Agent updated successfully');
      setEditOpen(false);
      setEditingAgent(null);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agent');
    }
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setEditFormData({
      name: agent.name,
      email: agent.email,
      phone: agent.phone || '',
      is_active: agent.is_active
    });
    setEditOpen(true);
  };

  const openResetPasswordDialog = (agent) => {
    setResetAgentId(agent.id);
    setResetAgentName(agent.name);
    setNewPasswordInput('');
    setResetResult(null);
    setShowNewPassword(false);
    setResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetAgentId) return;
    
    setResetting(true);
    try {
      const response = await api().post(`/agents/${resetAgentId}/reset-password`, {
        new_password: newPasswordInput || null
      });
      setResetResult(response.data.new_password);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this agent?')) return;
    try {
      await api().delete(`/agents/${id}`);
      toast.success('Agent deleted successfully');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const toggleAgentStatus = async (agent) => {
    try {
      await api().put(`/agents/${agent.id}`, { is_active: !agent.is_active });
      toast.success(`Agent ${agent.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchAgents();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAgents(agents.map(a => a.id));
    } else {
      setSelectedAgents([]);
    }
  };

  const handleSelectAgent = (agentId, checked) => {
    if (checked) {
      setSelectedAgents(prev => [...prev, agentId]);
    } else {
      setSelectedAgents(prev => prev.filter(id => id !== agentId));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedAgents.length} agent(s)?`)) return;
    try {
      await Promise.all(selectedAgents.map(id => api().delete(`/agents/${id}`)));
      toast.success(`${selectedAgents.length} agent(s) deleted`);
      setSelectedAgents([]);
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete some agents');
    }
  };

  const agentPortalUrl = `${window.location.origin}/agent-portal`;

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Agents</h1>
        <p className="text-muted-foreground">Manage external agents who can submit applications</p>
      </div>

      {/* Agent Portal Info */}
      <Alert>
        <UserPlus className="h-4 w-4" />
        <AlertTitle>Agent Portal</AlertTitle>
        <AlertDescription className="mt-2">
          <p className="mb-2">Agents can access the portal to submit applications at:</p>
          <div className="flex items-center gap-2">
            <code className="bg-muted px-2 py-1 rounded text-sm">{agentPortalUrl}</code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(agentPortalUrl)}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Registered Agents
            </CardTitle>
            <CardDescription>External agents who can submit applications</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {selectedAgents.length > 0 && (
              <>
                <span className="text-sm text-muted-foreground">{selectedAgents.length} selected</span>
                <Button variant="outline" size="sm" onClick={() => setSelectedAgents([])}>
                  Clear
                </Button>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </>
            )}
            <CSVExportButton entityType="agents" entityLabel="Agents" />
            <CSVImportModal entityType="agents" entityLabel="Agents" onSuccess={fetchAgents} />
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-agent-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agent
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Agent</DialogTitle>
                  <DialogDescription>Create a new external agent account. Set a custom password or leave blank to auto-generate.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Agent Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="agent-name-input"
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="agent-email-input"
                      placeholder="agent@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone (optional)</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="agent-phone-input"
                      placeholder="+1234567890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password (optional)</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        data-testid="agent-password-input"
                        placeholder="Leave blank to auto-generate"
                        minLength={6}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Minimum 6 characters. Leave blank to auto-generate a secure password.</p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      A welcome email with login credentials will be sent to the agent (requires SMTP configuration).
                    </p>
                  </div>
                  <Button type="submit" className="w-full" data-testid="agent-submit-btn">
                    Create Agent
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : agents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <UserPlus className="w-12 h-12 mb-4 opacity-50" />
              <p>No agents registered yet</p>
              <Button variant="link" onClick={() => setCreateOpen(true)}>
                Add your first agent
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={agents.length > 0 && selectedAgents.length === agents.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id} data-testid={`agent-row-${agent.id}`}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedAgents.includes(agent.id)}
                        onCheckedChange={(checked) => handleSelectAgent(agent.id, checked)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{agent.name}</TableCell>
                    <TableCell>{agent.email}</TableCell>
                    <TableCell>
                      <code className="bg-muted px-2 py-0.5 rounded text-sm">{agent.username}</code>
                    </TableCell>
                    <TableCell>{agent.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge className={agent.is_active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}>
                        {agent.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(agent.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`agent-actions-${agent.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(agent)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openResetPasswordDialog(agent)}>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleAgentStatus(agent)}>
                            <Shield className="w-4 h-4 mr-2" />
                            {agent.is_active ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(agent.id)} className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Credentials Dialog */}
      <Dialog open={credentialsOpen} onOpenChange={setCredentialsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Agent Created Successfully
            </DialogTitle>
            <DialogDescription>
              Share these credentials with the agent. Make sure to save the password as it cannot be recovered later.
            </DialogDescription>
          </DialogHeader>
          {newAgentCredentials && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Username</Label>
                <div className="flex items-center gap-2">
                  <Input value={newAgentCredentials.username} readOnly />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(newAgentCredentials.username)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newAgentCredentials.password}
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(newAgentCredentials.password)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Portal URL</Label>
                <div className="flex items-center gap-2">
                  <Input value={agentPortalUrl} readOnly />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(agentPortalUrl)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => {
              setCredentialsOpen(false);
              setNewAgentCredentials(null);
              setShowPassword(false);
            }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Agent</DialogTitle>
            <DialogDescription>Update agent details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Agent Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Active Status</Label>
              <Switch
                checked={editFormData.is_active}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_active: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={(open) => {
        setResetPasswordOpen(open);
        if (!open) {
          setResetResult(null);
          setNewPasswordInput('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Reset password for agent: <strong>{resetAgentName}</strong>
            </DialogDescription>
          </DialogHeader>
          
          {!resetResult ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New Password (optional)</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPasswordInput}
                    onChange={(e) => setNewPasswordInput(e.target.value)}
                    placeholder="Leave blank to auto-generate"
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Minimum 6 characters. Leave blank to auto-generate.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetPasswordOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={resetting}>
                  {resetting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert className="bg-green-500/10 border-green-500/30">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertTitle className="text-green-600">Password Reset Successful</AlertTitle>
                <AlertDescription className="text-green-600/80">
                  Share this new password with the agent.
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={resetResult}
                    readOnly
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(resetResult)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setResetPasswordOpen(false);
                  setResetResult(null);
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentsPage;
